import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import api from '../services/api';
import './AdminUsers.css';

interface ManagedUser {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  role: UserRole;
  company?: string;
  isApproved: boolean;
  isActive: boolean;
  createdAt: string;
}

const AdminUsers: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');

  useEffect(() => {
    if (user?.role !== UserRole.ADMIN) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (id: number) => {
    try {
      await api.patch(`/admin/users/${id}/approve`);
      setUsers(users.map(u => u.id === id ? { ...u, isApproved: true } : u));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to approve user');
    }
  };

  const rejectUser = async (id: number) => {
    try {
      await api.patch(`/admin/users/${id}/reject`);
      setUsers(users.map(u => u.id === id ? { ...u, isApproved: false } : u));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reject user');
    }
  };

  const toggleActive = async (id: number, currentActive: boolean) => {
    try {
      await api.patch(`/admin/users/${id}/toggle-active`);
      setUsers(users.map(u => u.id === id ? { ...u, isActive: !currentActive } : u));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update user');
    }
  };

  const deleteUser = async (id: number, name: string) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers(users.filter(u => u.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const filtered = users.filter(u => {
    if (filter === 'pending') return !u.isApproved;
    if (filter === 'approved') return u.isApproved;
    return true;
  });

  const pendingCount = users.filter(u => !u.isApproved).length;

  const roleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ARCHITECT: return 'Architect';
      case UserRole.CLIENT: return 'Client';
      case UserRole.VENDOR: return 'Vendor';
      default: return role;
    }
  };

  return (
    <div className="admin-users-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>arcivi</h1>
          <span className="admin-badge">Admin Panel</span>
        </div>
        <div className="admin-header-right">
          <button onClick={() => navigate('/dashboard')} className="btn-ghost">Dashboard</button>
          <button onClick={logout} className="btn-ghost">Logout</button>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-page-title">
          <h2>User Management</h2>
          {pendingCount > 0 && (
            <span className="pending-badge">{pendingCount} pending approval</span>
          )}
        </div>

        <div className="filter-tabs">
          <button
            className={filter === 'all' ? 'tab active' : 'tab'}
            onClick={() => setFilter('all')}
          >
            All ({users.length})
          </button>
          <button
            className={filter === 'pending' ? 'tab active' : 'tab'}
            onClick={() => setFilter('pending')}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={filter === 'approved' ? 'tab active' : 'tab'}
            onClick={() => setFilter('approved')}
          >
            Approved ({users.length - pendingCount})
          </button>
        </div>

        {loading && <div className="loading">Loading users...</div>}
        {error && <div className="error-msg">{error}</div>}

        {!loading && filtered.length === 0 && (
          <div className="empty-state">No users found.</div>
        )}

        <div className="users-grid">
          {filtered.map(u => (
            <div key={u.id} className={`user-card ${!u.isApproved ? 'pending' : ''} ${!u.isActive ? 'inactive' : ''}`}>
              <div className="user-card-header">
                <div className="user-avatar">{u.name.charAt(0).toUpperCase()}</div>
                <div className="user-info">
                  <h3>{u.name}</h3>
                  <span className={`role-tag role-${u.role}`}>{roleLabel(u.role)}</span>
                </div>
                <div className="user-status">
                  {u.isApproved ? (
                    <span className="status approved">Approved</span>
                  ) : (
                    <span className="status pending">Pending</span>
                  )}
                  {!u.isActive && <span className="status inactive">Inactive</span>}
                </div>
              </div>

              <div className="user-details">
                {u.phone && <p><strong>Phone:</strong> {u.phone}</p>}
                {u.email && <p><strong>Email:</strong> {u.email}</p>}
                {u.company && <p><strong>Company:</strong> {u.company}</p>}
                <p><strong>Joined:</strong> {new Date(u.createdAt).toLocaleDateString()}</p>
              </div>

              <div className="user-actions">
                {!u.isApproved ? (
                  <button className="btn-approve" onClick={() => approveUser(u.id)}>Approve</button>
                ) : (
                  <button className="btn-reject" onClick={() => rejectUser(u.id)}>Revoke</button>
                )}
                <button
                  className={u.isActive ? 'btn-deactivate' : 'btn-activate'}
                  onClick={() => toggleActive(u.id, u.isActive)}
                >
                  {u.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn-delete" onClick={() => deleteUser(u.id, u.name)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
